import type {EventStats, EventStatSeries, EventTicketingFeeSplit, TimedRecentAction} from "@Components/mystuff-event-promotion-page/mystuff-event-promotion-page.types";
import {MAX_RECENT_ACTIONS, PROMOTION_METRIC_DRAWER_TABS, PromotionType} from "@Components/mystuff-event-promotion-page/mystuff-event-promotion-page.types";
import {getAssetUrl} from "@Utils/s3.util";
import type {EventVO} from '@Components/events/events.types';
import type {EarningsPayout, EarningsRow} from '@Components/mystuff-event-promotion-page/components/promotion-performance-drawer/components/performance-earnings-tab/performance-earnings-tab.types';
import type {ReachTile, RecentAction} from '@Components/mystuff-event-promotion-page/components/promotion-performance-drawer/components/performance-reach-tab/performance-reach-tab.types';
import type {PromotionMetricsRowSlots, PromotionMetricType} from '@Components/mystuff-event-promotion-page/components/promotion-metrics-row/promotion-metrics-row.types';
import type {PerformanceDrawerTab} from '@Components/mystuff-event-promotion-page/components/promotion-performance-drawer/promotion-performance-drawer.types';
import {PERFORMANCE_DRAWER_TAB_ORDER} from '@Components/mystuff-event-promotion-page/components/promotion-performance-drawer/promotion-performance-drawer.types';
import {
  DEFAULT_CURRENCY_CODE,
  DEFAULT_PROMOTION_ROW_METRICS,
  PROMOTION_ROW_METRICS_WITHOUT_EARNINGS,
} from '@Components/mystuff-event-promotion-page/components/promotion-metrics-row/promotion-metrics-row.types';
import {isPaidTicketedEvent} from '@Libraries/event-ticketing.library';
import {isPAYGUser} from '@Utils/user.util';
import {formatNumber, formatNumberInCurrency} from '@Utils/number.util';
import {formatShortUSDate, getTimeAndDateLabel} from '@Utils/date.util';
import {formatTicketFeeRate, getTicketingNextPayoutDate} from '@Components/event-wizard/event-wizard-ticketing/ticketing-fees.helper';
import {getUTCEventEndDate} from '@Libraries/events.library';
import {assertNever} from '@Utils/assert-never.util';
import {ICON_COMPLETED} from '@Components/mystuff-event-promotion-page/components/promotion-checklist-item/promotion-checklist-item.types';
import type {PlatformMediaType, SocialMediaAccountIdString, SocialPost} from '@Components/social-media/post.vo';
import type {SocialPlatformMetrics} from '@Components/social-media/social-platform-metrics.types';
import type {EmailCampaign} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import {isPostPublished} from '@Libraries/social-media-post.library';
import {isCampaignDeleted, isCampaignSent} from '@Libraries/email-campaigns-library';

const getEmptyEventStatSeries = (): EventStatSeries => ({value: 0, series: [], percentageChange: null, currency: null});

/** Zeroed placeholder for before the first stats fetch resolves — real figures replace it on load. */
export const getEmptyEventStats = (): EventStats => ({
  views: 0,
  embedViews: 0,
  registrations: 0,
  reach: 0,
  emailReach: 0,
  reachSeries: getEmptyEventStatSeries(),
  earningsSeries: getEmptyEventStatSeries(),
  registrationsSeries: getEmptyEventStatSeries(),
  viewsSeries: getEmptyEventStatSeries(),
  feeSplit: null,
});

/**
 * SHARE_EVENT_PAGE_LINK arms in the three switches below are unreachable — deprecated backend-side, never served. Kept per D10.
 * EMBED_IN_WEBPAGE's image arm is reachable (the embed card); its text arms are not — `getTextForPromotionType`'s only caller maps counted steps, and `getDescriptiveTextForPromotionType` has none.
 */
export const getTextForPromotionType = (eventPromotionType: PromotionType): string => {
    switch (eventPromotionType) {
        case PromotionType.EVENT_PAGE:
            return window.i18next.t('pmwjs_create_event_page');
        case PromotionType.SHARE_EVENT_PAGE_LINK:
            return window.i18next.t('pmwjs_share_your_event_link');
        case PromotionType.SOCIAL_POSTS:
            return window.i18next.t('pmwjs_publish_on_social_media');
        case PromotionType.EMAIL_CAMPAIGNS:
            return window.i18next.t('pmwjs_send_an_email_campaign');
        case PromotionType.EMBED_IN_WEBPAGE:
            return window.i18next.t('pmwjs_add_to_your_website_or_blog');
        case PromotionType.PRINT_FLYERS:
            return window.i18next.t('pmwjs_download_and_print_flyers_without_ampersand');

        default:
            return '';
    }
};

/** A step's own icon in the checklist popover. Uncounted types reuse their card's icon, since the server decides which steps are counted. */
export const getIconForPromotionType = (eventPromotionType: PromotionType): string => {
    switch (eventPromotionType) {
        case PromotionType.EVENT_PAGE:
            return ICON_COMPLETED;
        case PromotionType.SOCIAL_POSTS:
            return 'icon-social-media';
        case PromotionType.EMAIL_CAMPAIGNS:
            return 'icon-envelope';
        case PromotionType.PRINT_FLYERS:
            return 'icon-designs';
        case PromotionType.SHARE_EVENT_PAGE_LINK:
            return 'icon-link';
        case PromotionType.EMBED_IN_WEBPAGE:
            return 'icon-code';
        case PromotionType.SETUP_CALENDAR:
        case PromotionType.PROMOTE_CALENDAR:
            return 'icon-calendar';
        default:
            return assertNever(eventPromotionType);
    }
};


export const getDescriptiveTextForPromotionType = (eventPromotionType: PromotionType): string => {
    switch (eventPromotionType) {
        case PromotionType.EVENT_PAGE:
            return '';
        case PromotionType.SHARE_EVENT_PAGE_LINK:
            return window.i18next.t('pmwjs_reach_more_people_with_a_shareable_link');
        case PromotionType.SOCIAL_POSTS:
            return window.i18next.t('pmwjs_publish_to_your_favorite_platform_in_seconds');
        case PromotionType.EMAIL_CAMPAIGNS:
            return window.i18next.t('pmwjs_grow_attendance_with_quick_and_easy_emails');
        case PromotionType.EMBED_IN_WEBPAGE:
            return window.i18next.t('pmwjs_seamlessly_showcase_your_event_and_calendar_on_your_site');
        case PromotionType.PRINT_FLYERS:
            return window.i18next.t('pmwjs_grab_attention_with_bold_flyers');

        default:
            return '';
    }
};

/** The event's ticketing currency — the same fallback chain the metrics row uses before any sale has priced it. */
const getEventTicketingCurrency = (event: EventVO, stats: EventStats): string => {
  return stats.earningsSeries.currency ?? event.ticketingListStats?.currency ?? DEFAULT_CURRENCY_CODE;
};

const sumReachAcrossMediaTypes = (metricsByMediaType: Partial<Record<PlatformMediaType, SocialPlatformMetrics>>): number => {
  return Object.values(metricsByMediaType).reduce((total, metrics) => total + (metrics?.numReach ?? 0), 0);
};

const sumReachAcrossAccounts = (metricsByAccount: Record<SocialMediaAccountIdString, Partial<Record<PlatformMediaType, SocialPlatformMetrics>>>): number => {
  return Object.values(metricsByAccount).reduce((total, metricsByMediaType) => total + sumReachAcrossMediaTypes(metricsByMediaType), 0);
};

/** Total reach the event's social posts have driven, summed across every account and media type they were published to. */
const getSocialMediaReachTotal = (socialPosts: SocialPost[]): number => {
  return socialPosts.reduce((total, post) => total + sumReachAcrossAccounts(post.metrics ?? {}), 0);
};

/** `5407:48127`: only an event that is embedded somewhere gets the tile, and a view is the sign it is. */
const getEmbeddedViewsTiles = (stats: EventStats): ReachTile[] => {
  if (stats.embedViews === 0) {
    return [];
  }

  return [{label: window.i18next.t('pmwjs_embedded_views'), value: formatNumber(stats.embedViews), iconClassName: 'icon-globe'}];
};

/**
 * Performance drawer Reach tab tiles, in the frame's order (D21). Social media sums `numReach` off the posts
 * already on this page's own response (`socialPosts[].metrics`, decoded server-side in `composeSocialPost`).
 */
export const getPerformanceReachTiles = (stats: EventStats, socialPosts: SocialPost[]): ReachTile[] => {
  return [
    {label: window.i18next.t('pmwjs_social_media'), value: formatNumber(getSocialMediaReachTotal(socialPosts)), iconClassName: 'icon-share'},
    {label: window.i18next.t('pmwjs_email_campaigns'), value: formatNumber(stats.emailReach), iconClassName: 'icon-envelope'},
    ...getEmbeddedViewsTiles(stats),
    {label: window.i18next.t('pmwjs_page_views'), value: formatNumber(stats.views), iconClassName: 'icon-eye'},
  ];
};

/** The Reach tab's Recent actions feed, newest first: sent emails, published posts and the event's creation. */
export const getPerformanceRecentActions = (event: EventVO, socialPosts: SocialPost[], emailCampaigns: EmailCampaign[]): RecentAction[] => {
  return [...getSentEmailRecentActions(emailCampaigns), ...getPublishedPostRecentActions(socialPosts), getEventCreatedRecentAction(event)]
    .sort((first, second) => second.timestampInSeconds - first.timestampInSeconds)
    .slice(0, MAX_RECENT_ACTIONS)
    .map((timedRecentAction) => timedRecentAction.recentAction);
};

const getSentEmailRecentActions = (emailCampaigns: EmailCampaign[]): TimedRecentAction[] => {
  return emailCampaigns
    .filter((campaign) => isCampaignSent(campaign) && !isCampaignDeleted(campaign))
    .map((campaign) => buildTimedRecentAction(getSentEmailRecentActionTitle(campaign), 'icon-envelope', campaign.publishOn));
};

const getSentEmailRecentActionTitle = (campaign: EmailCampaign): string => {
  const [firstMailingListName] = campaign.mailingListNames;
  if (firstMailingListName === undefined) {
    return window.i18next.t('pmwjs_sent_an_email_campaign');
  }

  if (campaign.mailingListNames.length === 1) {
    return window.i18next.t('pmwjs_sent_email_to_mailing_list', {mailingListName: firstMailingListName});
  }

  return window.i18next.t('pmwjs_sent_email_to_x_mailing_lists', {number: campaign.mailingListNames.length});
};

/** A post with no publish time is dated by its creation, and dropped if it has neither. */
const getPublishedPostRecentActions = (socialPosts: SocialPost[]): TimedRecentAction[] => {
  return socialPosts.flatMap((post) => {
    const publishedOn = post.publishOn ?? post.createdOn;
    if (!isPostPublished(post.state) || publishedOn === undefined) {
      return [];
    }
    return [buildTimedRecentAction(window.i18next.t('pmwjs_posted_on_social_media'), 'icon-share', publishedOn)];
  });
};

const getEventCreatedRecentAction = (event: EventVO): TimedRecentAction => {
  return buildTimedRecentAction(window.i18next.t('pmwjs_created_the_event'), 'icon-event', event.createdOnTimestamp);
};

const buildTimedRecentAction = (title: string, iconClassName: string, timestampInSeconds: number): TimedRecentAction => {
  return {timestampInSeconds, recentAction: {iconClassName, title, timestamp: getTimeAndDateLabel(timestampInSeconds)}};
};

/** The processing fee's rate line ("3% + $0.30 per order"); none when the fee split is withheld. */
const getProcessingFeeRateText = (feeSplit: EventTicketingFeeSplit | null): string | undefined => {
  if (feeSplit === null) {
    return undefined;
  }

  return window.i18next.t('pmwjs_event_ticketing_fee_per_order', {rate: formatTicketFeeRate(feeSplit.processingFeePercent, feeSplit.processingFeeFixed)});
};

/** Performance drawer Earnings rows: Total sales (net ticket sales), Service fee, and Processing fee with its rate line. */
export const getPerformanceEarningsRows = (event: EventVO, stats: EventStats): EarningsRow[] => {
  const currency = getEventTicketingCurrency(event, stats);
  const grossSales = event.ticketingListStats?.sales ?? 0;
  const serviceFee = stats.feeSplit?.platformFee ?? 0;
  const processingFee = stats.feeSplit?.processingFee ?? 0;
  return [
    {label: window.i18next.t('pmwjs_ticketing_stat_total_sales'), value: formatNumberInCurrency(grossSales, currency)},
    {label: window.i18next.t('pmwjs_event_ticketing_service_fee'), value: formatNumberInCurrency(serviceFee, currency)},
    {label: window.i18next.t('pmwjs_event_ticketing_processing_fee'), subLabel: getProcessingFeeRateText(stats.feeSplit), value: formatNumberInCurrency(processingFee, currency)},
  ];
};

/** Performance drawer payout line — net earnings (never gross), with the next payout date that can still pay the event once there is anything to pay out. */
export const getPerformanceEarningsPayout = (event: EventVO, stats: EventStats): EarningsPayout => {
  const currency = getEventTicketingCurrency(event, stats);
  const netEarnings = stats.earningsSeries.value;
  const nextPayoutLabel =
    netEarnings > 0 ? window.i18next.t('pmwjs_event_ticketing_next_payout', {date: formatShortUSDate(getTicketingNextPayoutDate(getUTCEventEndDate(event), new Date()), 'UTC')}) : '';
  return {label: window.i18next.t('pmwjs_event_ticketing_your_payout'), value: formatNumberInCurrency(netEarnings, currency), nextPayoutLabel};
};

/** Earnings shows only on events that can earn, to viewers allowed to see money or as a PAYG upsell (D51). */
export const getPromotionMetricsRowSlots = (event: EventVO, stats: EventStats, canViewEarnings: boolean): PromotionMetricsRowSlots => {
  const canEventEarn = isPaidTicketedEvent(event) || stats.earningsSeries.value > 0;
  return (canViewEarnings || isPAYGUser()) && canEventEarn ? DEFAULT_PROMOTION_ROW_METRICS : PROMOTION_ROW_METRICS_WITHOUT_EARNINGS;
};

/** The drawer tabs for the metrics the row shows, so the drawer never offers a tab whose card is hidden. */
export const getPerformanceDrawerTabsForMetrics = (metrics: PromotionMetricType[]): PerformanceDrawerTab[] => {
  return PERFORMANCE_DRAWER_TAB_ORDER.filter((tab) => metrics.some((metric) => PROMOTION_METRIC_DRAWER_TABS[metric] === tab));
};

/** The phone card thumbnails are Figma's `.promotion-cards-mobile` thumbnails (`5388:45820`–`5388:45826`), exported at 3x. */
export const getImageURLForPromotionType = (eventPromotionType: PromotionType): string => {
    switch (eventPromotionType) {
        case PromotionType.EVENT_PAGE:
            return getAssetUrl('event-promotion-create-page-graphic.png');
        case PromotionType.SHARE_EVENT_PAGE_LINK:
            return getAssetUrl('event-promotion-share-link-incomplete-state-graphic.png');
        case PromotionType.SOCIAL_POSTS:
            return getAssetUrl('event-promotion-card-thumbnail-social-posts.png');
        case PromotionType.EMAIL_CAMPAIGNS:
            return getAssetUrl('event-promotion-card-thumbnail-email-campaigns.png');
        case PromotionType.EMBED_IN_WEBPAGE:
            return getAssetUrl('event-promotion-card-thumbnail-embed.png');
        case PromotionType.PRINT_FLYERS:
            return getAssetUrl('event-promotion-card-thumbnail-print-flyers.png');
        case PromotionType.PROMOTE_CALENDAR:
            return getAssetUrl('event-promotion-card-thumbnail-promote-calendar.png');

        default:
            return '';
    }
};

/** A post's caption for its promote preview: its first account's post or reel caption, else its own, else "No caption". */
export const getPromotionSocialPostCaption = (post: SocialPost): string => {
  const [firstAccount] = post.accounts;
  const firstAccountPublishingParams = firstAccount === undefined ? undefined : post.publishingParams[firstAccount.id];
  const publishedCaption = (firstAccountPublishingParams?.POST ?? firstAccountPublishingParams?.REELS)?.caption;
  if (publishedCaption) {
    return publishedCaption;
  }

  if (Object.keys(post.publishingParams).length === 0 && post.caption) {
    return post.caption;
  }

  return window.i18next.t('pmwjs_no_caption');
};
